// Portal users of a customer / supplier (legacy "Users" modal in the list):
//   GET {res}/users/{id} · POST {res}/add_user · PUT {res}/update_user/{user} · DELETE {res}/delete_user/{user}
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, Divider, FormControlLabel, Stack, TextField, Typography } from '@mui/material';

import { adminApi, apiErrorMessage, apiValidationErrors } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { t } from '../../i18n';

type PortalUser = { id: string; name?: string; username?: string; email?: string; phone?: string; active?: number | boolean };
type Errors = Record<string, string[]>;

const emptyForm = { id: '', name: '', phone: '', email: '', username: '', password: '', password_confirmation: '', active: 1 };

export function PortalUsersDialog({
  resource,
  partyId,
  startWithForm = false,
  onClose
}: {
  resource: 'customers' | 'suppliers';
  partyId: string | number;
  startWithForm?: boolean;
  onClose: () => void;
}) {
  const partyKey = resource === 'customers' ? 'customer_id' : 'supplier_id';
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<typeof emptyForm | null>(startWithForm ? { ...emptyForm } : null);
  const [errors, setErrors] = useState<Errors>({});
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`${resource}/users/${partyId}`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, t('common.unableToLoad', { name: t('parties.portalUsers') })), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [partyId, resource]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = (key: keyof typeof emptyForm, value: unknown) => setForm((current) => (current ? { ...current, [key]: value } : current));

  const save = async () => {
    if (!form) return;
    setErrors({});
    setMessage(null);
    if (!form.email && !form.phone) {
      setErrors({ email: [t('parties.emailOrPhone')] });
      return;
    }
    if (!form.id && form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: [t('parties.passwordMismatch')] });
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        phone: form.phone || null,
        email: form.email || null,
        username: form.username,
        active: form.active ? 1 : 0,
        employee: 0,
        [partyKey]: partyId
      };
      if (!form.id) {
        payload.password = form.password;
        payload.password_confirmation = form.password_confirmation;
      }
      const response = form.id
        ? await adminApi.put(`${resource}/update_user/${form.id}`, { ...payload, id: form.id })
        : await adminApi.post(`${resource}/add_user`, payload);
      if (response.data?.success === false) throw new Error(t('common.saveFailed'));
      setForm(null);
      setMessage({ text: form.id ? t('parties.recordUpdated') : t('parties.recordAdded'), severity: 'success' });
      await load();
    } catch (err) {
      const validation = apiValidationErrors(err);
      setErrors(validation);
      if (!Object.keys(validation).length) setMessage({ text: apiErrorMessage(err, t('common.saveFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form?.id) return;
    setSaving(true);
    try {
      const response = await adminApi.delete(`${resource}/delete_user/${form.id}`);
      if (!response.data?.success) throw new Error(t('common.deleteFailed'));
      setForm(null);
      setMessage({ text: t('parties.recordDeleted'), severity: 'success' });
      await load();
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, t('common.deleteFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key: string) => errors[key]?.[0];

  return (
    <Stack spacing={2} sx={{ minWidth: { sm: 460 } }}>
      {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
      {form ? (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{form.id ? t('parties.editUser') : t('parties.addUser')}</Typography>
          <TextField size="small" label={t('field.name')} required value={form.name} onChange={(e) => set('name', e.target.value)} error={Boolean(fieldError('name'))} helperText={fieldError('name')} />
          <TextField size="small" label={t('field.phone')} value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} error={Boolean(fieldError('phone'))} helperText={fieldError('phone')} />
          <TextField size="small" label={t('field.email')} value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} error={Boolean(fieldError('email'))} helperText={fieldError('email')} />
          <TextField size="small" label={t('field.username')} required value={form.username} onChange={(e) => set('username', e.target.value)} error={Boolean(fieldError('username'))} helperText={fieldError('username')} />
          {!form.id ? (
            <>
              <TextField size="small" type="password" label={t('parties.newPassword')} required value={form.password} onChange={(e) => set('password', e.target.value)} error={Boolean(fieldError('password'))} helperText={fieldError('password')} />
              <TextField size="small" type="password" label={t('parties.confirmPassword')} required value={form.password_confirmation} onChange={(e) => set('password_confirmation', e.target.value)} error={Boolean(fieldError('password_confirmation'))} helperText={fieldError('password_confirmation')} />
            </>
          ) : null}
          <FormControlLabel control={<Checkbox checked={Boolean(form.active)} onChange={(e) => set('active', e.target.checked ? 1 : 0)} />} label={t('field.active')} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {form.id ? (
              <Button color="error" disabled={saving} onClick={() => void remove()}>
                {t('action.delete')}
              </Button>
            ) : null}
            <Button disabled={saving} onClick={() => setForm(null)}>
              {t('action.cancel')}
            </Button>
            <Button variant="contained" disabled={saving} onClick={() => void save()}>
              {t('action.save')}
            </Button>
          </Stack>
          <Divider />
        </Stack>
      ) : null}
      {loading ? (
        <LoadingBlock />
      ) : users.length ? (
        <Stack spacing={1}>
          {users.map((user) => (
            <Box
              key={user.id}
              role="button"
              onClick={() => setForm({ ...emptyForm, ...user, phone: user.phone ?? '', email: user.email ?? '', username: user.username ?? '', name: user.name ?? '', active: user.active ? 1 : 0, password: '', password_confirmation: '' })}
              sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                  {user.name} ({user.username})
                </Typography>
                <Chip size="small" color={user.active ? 'success' : 'default'} label={user.active ? t('status.yes') : t('status.no')} />
              </Stack>
              {user.phone ? <Typography variant="caption" display="block">{t('field.phone')}: {user.phone}</Typography> : null}
              {user.email ? <Typography variant="caption" display="block">{t('field.email')}: {user.email}</Typography> : null}
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('parties.noData')}
        </Typography>
      )}
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button disabled={Boolean(form)} onClick={() => setForm({ ...emptyForm })}>
          {t('parties.addUser')}
        </Button>
        <Button onClick={onClose}>{t('action.close')}</Button>
      </Stack>
    </Stack>
  );
}
